import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabase';

interface ConteudoAfiliados {
  titulo_hero: string;
  subtitulo_hero: string;
  titulo_programa: string;
  descricao_programa: string;
  titulo_regras: string;
  regra1_titulo: string;
  regra1_descricao: string;
  regra2_titulo: string;
  regra2_descricao: string;
  regra3_titulo: string;
  regra3_descricao: string;
  titulo_cta: string;
  descricao_cta: string;
  texto_botao: string;
}

const AfiliadosPage = () => {
  const [conteudo, setConteudo] = useState<ConteudoAfiliados | null>(null);
  const [whatsappNumero, setWhatsappNumero] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      // Carregar conteúdo da página
      const { data: conteudoData } = await supabase
        .from('conteudo_afiliados')
        .select('*')
        .maybeSingle();

      if (conteudoData) {
        setConteudo(conteudoData);
      }

      // Carregar número de WhatsApp
      const { data: whatsappData } = await supabase
        .from('whatsapp_config')
        .select('numero_whatsapp, ativo')
        .maybeSingle();

      if (whatsappData?.ativo && whatsappData?.numero_whatsapp) {
        setWhatsappNumero(whatsappData.numero_whatsapp);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCandidatura = () => {
    if (whatsappNumero) {
      const numeroLimpo = whatsappNumero.replace(/\D/g, '');
      window.open(`https://wa.me/${numeroLimpo}?text=Olá! Gostaria de me candidatar ao programa de afiliados da Dindimcast.`, '_blank');
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const regras = conteudo ? [
    {
      icon: 'ri-calendar-check-line',
      titulo: conteudo.regra1_titulo,
      descricao: conteudo.regra1_descricao
    },
    {
      icon: 'ri-shield-check-line',
      titulo: conteudo.regra2_titulo,
      descricao: conteudo.regra2_descricao
    },
    {
      icon: 'ri-focus-3-line',
      titulo: conteudo.regra3_titulo,
      descricao: conteudo.regra3_descricao
    }
  ] : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-luxury-black">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="w-16 h-16 border-4 border-luxury-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-luxury-black">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-8 lg:px-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-luxury-gold/10 via-transparent to-luxury-gold/5"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <div className="w-20 h-20 bg-gradient-gold rounded-full flex items-center justify-center mx-auto">
              <i className="ri-team-line text-4xl text-black"></i>
            </div>
          </motion.div>

          <h1 className="text-5xl lg:text-6xl font-serif font-bold text-white mb-6">
            {conteudo?.titulo_hero || 'Torna-te um Afiliado Oficial Dindimcast'}
          </h1>

          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {conteudo?.subtitulo_hero || 'Queres fazer parte da equipa que está a transformar o mercado da educação digital?'}
          </p>
        </motion.div>
      </section>

      {/* Como Funciona Section */}
      <section className="py-20 px-8 lg:px-20 bg-luxury-black/50">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-6xl mx-auto"
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
              {conteudo?.titulo_programa || 'Como funciona o nosso Programa?'}
            </h2>
            <div className="w-24 h-1 bg-gradient-gold mx-auto mb-8"></div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="bg-gradient-to-br from-luxury-gold/5 to-luxury-gold/10 border border-luxury-gold/20 rounded-2xl p-10 lg:p-16"
          >
            <p className="text-lg text-gray-200 leading-relaxed mb-8">
              {conteudo?.descricao_programa || 'Como afiliado, terás acesso a comissões competitivas e a produtos com alta taxa de conversão.'}
            </p>
            
            <div className="bg-luxury-black/50 border border-luxury-gold/30 rounded-xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-gold rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-black"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-luxury-gold mb-3">Importante</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Para manter a qualidade da nossa marca, a nossa afiliação é <strong className="text-white">restrita e baseada em mérito</strong>. Procuramos apenas os melhores parceiros que estejam alinhados com os nossos valores e padrões de excelência.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Regras de Ouro Section */}
      <section className="py-20 px-8 lg:px-20">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-6xl mx-auto"
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
              {conteudo?.titulo_regras || 'As Nossas Regras de Ouro'}
            </h2>
            <div className="w-24 h-1 bg-gradient-gold mx-auto mb-8"></div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Estes são os pilares fundamentais que todos os nossos afiliados devem seguir para garantir o sucesso mútuo.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {regras.map((regra, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
                className="bg-gradient-to-br from-luxury-gold/5 to-luxury-gold/10 border border-luxury-gold/20 rounded-2xl p-8 hover:border-luxury-gold/40 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gradient-gold rounded-xl flex items-center justify-center mb-6">
                  <i className={`${regra.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{regra.titulo}</h3>
                <p className="text-gray-300 leading-relaxed">{regra.descricao}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-8 lg:px-20 bg-gradient-to-br from-luxury-gold/10 via-luxury-black to-luxury-gold/5">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-4xl mx-auto text-center"
        >
          <motion.div variants={fadeInUp}>
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
              {conteudo?.titulo_cta || 'Como te podes candidatar?'}
            </h2>
            <div className="w-24 h-1 bg-gradient-gold mx-auto mb-8"></div>
          </motion.div>

          <motion.p
            variants={fadeInUp}
            className="text-xl text-gray-200 leading-relaxed mb-12"
          >
            {conteudo?.descricao_cta || 'Se tens sede de resultados e estás disposto a seguir as nossas regras para faturares alto, o próximo passo é uma conversa direta connosco.'}
          </motion.p>

          <motion.div
            variants={fadeInUp}
            className="bg-luxury-black/50 border border-luxury-gold/30 rounded-2xl p-10 mb-10"
          >
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              Para iniciares o teu processo de aprovação, clica no botão abaixo e envia-nos uma mensagem oficial através do nosso <strong className="text-luxury-gold">WhatsApp</strong> ou contacta-nos diretamente pelas nossas redes sociais.
            </p>

            <motion.button
              onClick={handleCandidatura}
              disabled={!whatsappNumero}
              whileHover={{ scale: whatsappNumero ? 1.05 : 1 }}
              whileTap={{ scale: whatsappNumero ? 0.95 : 1 }}
              className={`inline-flex items-center gap-3 px-10 py-4 text-lg font-bold rounded-full transition-all duration-300 whitespace-nowrap ${
                whatsappNumero 
                  ? 'bg-gradient-gold text-black hover:shadow-gold cursor-pointer' 
                  : 'bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              {conteudo?.texto_botao || 'Candidatar-me Agora'}
            </motion.button>

            {!whatsappNumero && (
              <p className="text-yellow-400 text-sm mt-4">
                <i className="ri-information-line mr-1"></i>
                Número de WhatsApp não configurado
              </p>
            )}
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="flex items-center justify-center gap-3 text-luxury-gold"
          >
            <i className="ri-time-line text-2xl"></i>
            <p className="text-base">
              Estamos à tua espera para começarmos a <strong>faturar juntos</strong>
            </p>
          </motion.div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default AfiliadosPage;
